import React, { useState, useRef, useEffect } from 'react';
import { Card, Input, Button, Space, Spin, Tag, Empty, Switch, message } from 'antd';
import { SendOutlined, RobotOutlined, UserOutlined, BulbOutlined, NotificationOutlined } from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import { apiFetch } from '../utils/api.js';
import { BRAND } from '../utils/chartTheme.js';

const SUGGESTIONS = [
  '分析8月各店铺销售情况',
  '哪个商品卖得最好',
  '8月总营收和利润是多少',
  '有哪些低库存商品',
  '8月售后情况怎么样',
  '对比两个店铺8月表现',
];

// 简单 markdown 渲染：**加粗**、#标题、- 列表
function renderText(text) {
  const lines = String(text || '').split('\n');
  const out = [];
  lines.forEach((line, i) => {
    const t = line.trim();
    if (!t) return;
    if (t.startsWith('### ')) out.push(<div key={i} style={{ fontWeight: 700, fontSize: 14, margin: '6px 0 2px', color: 'var(--brand-strong)' }}>{t.slice(4)}</div>);
    else if (t.startsWith('## ')) out.push(<div key={i} style={{ fontWeight: 700, fontSize: 15, margin: '6px 0 2px', color: 'var(--brand-strong)' }}>{t.slice(3)}</div>);
    else if (t.startsWith('# ')) out.push(<div key={i} style={{ fontWeight: 700, fontSize: 16, margin: '6px 0 2px', color: 'var(--brand-strong)' }}>{t.slice(2)}</div>);
    else if (t.startsWith('- ') || t.startsWith('* ')) out.push(<div key={i} style={{ paddingLeft: 12, position: 'relative' }}><span style={{ position: 'absolute', left: 0 }}>•</span>{inline(t.slice(2))}</div>);
    else out.push(<div key={i} style={{ margin: '2px 0' }}>{inline(t)}</div>);
  });
  return out;
}
function inline(t) {
  const parts = t.split(/\*\*(.+?)\*\*/g);
  return parts.map((p, i) => i % 2 === 1 ? <b key={i}>{p}</b> : p);
}

function ChartBlock({ chart }) {
  if (chart.type === 'pie') {
    const option = {
      tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
      series: [{ type: 'pie', radius: ['35%', '70%'], data: chart.data.map(d => ({ name: d.name, value: d.value })) }],
      color: [BRAND, '#E8A87C', '#F4D58D', '#8E9AAF', '#7FB3D5', '#B99274'],
    };
    return <ReactECharts option={option} theme="brand" style={{ height: 240 }} />;
  }
  if (chart.type === 'bar') {
    const option = {
      tooltip: { trigger: 'axis' },
      grid: { left: 60, right: 20, top: 30, bottom: 50 },
      xAxis: { type: 'category', data: chart.data.map(d => String(d.name).length > 10 ? String(d.name).slice(0, 10) + '…' : d.name), axisLabel: { rotate: 30, fontSize: 10 } },
      yAxis: { type: 'value' },
      series: [{ type: 'bar', data: chart.data.map(d => d.value), itemStyle: { color: BRAND }, barMaxWidth: 34 }],
    };
    return <ReactECharts option={option} theme="brand" style={{ height: 260 }} />;
  }
  const option = {
    tooltip: { trigger: 'axis' },
    legend: { top: 0, data: ['营收', '利润'] },
    grid: { left: 60, right: 20, top: 30, bottom: 40 },
    xAxis: { type: 'category', data: chart.data.map(d => d.name) },
    yAxis: { type: 'value' },
    series: [
      { name: '营收', type: 'line', data: chart.data.map(d => d.value), itemStyle: { color: BRAND }, smooth: true, areaStyle: { opacity: 0.12 } },
      { name: '利润', type: 'line', data: chart.data.map(d => d.profit || 0), itemStyle: { color: '#CF1322' }, smooth: true },
    ],
  };
  return <ReactECharts option={option} style={{ height: 260 }} />;
}

export default function AiAssistant() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  // 微信日报推送（PushPlus）配置
  const [pushCfg, setPushCfg] = useState({ configured: false, enabled: false, token_masked: '' });
  const [pushToken, setPushToken] = useState('');
  const [pushSaving, setPushSaving] = useState(false);
  const [pushTesting, setPushTesting] = useState(false);
  const [pushOpen, setPushOpen] = useState(false);

  const loadPushCfg = () => {
    apiFetch('/api/push-config').then(r => r.json()).then(d => setPushCfg(d.data || {})).catch(() => {});
  };
  useEffect(() => { loadPushCfg(); }, []);

  const savePushCfg = async () => {
    setPushSaving(true);
    try {
      const body = { enabled: pushCfg.enabled };
      if (pushToken.trim()) body.token = pushToken.trim();
      const res = await apiFetch('/api/push-config', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).then(r => r.json());
      if (res.success) { message.success('已保存推送配置'); setPushToken(''); loadPushCfg(); }
      else message.error(res.error || '保存失败');
    } catch (e) { message.error('保存失败'); }
    setPushSaving(false);
  };
  const testPush = async () => {
    setPushTesting(true);
    try {
      const res = await apiFetch('/api/push-test', { method: 'POST' }).then(r => r.json());
      if (res.success) message.success('测试日报已推送到微信，请查收');
      else message.error(res.error || '推送失败');
    } catch (e) { message.error('推送失败'); }
    setPushTesting(false);
  };

  useEffect(() => { if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight; }, [messages, loading]);

  const send = async (text) => {
    const q = (text || input).trim();
    if (!q || loading) return;
    const history = messages.map(m => ({ role: m.role, content: m.text }));
    const userMsg = { role: 'user', text: q, charts: [] };
    setMessages([...messages, userMsg]);
    setInput(''); setLoading(true);
    try {
      const res = await apiFetch('/api/ai/chat', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: [...history, { role: 'user', content: q }] }),
      }).then(r => r.json());
      if (res.success) {
        setMessages(prev => [...prev, { role: 'assistant', text: res.reply, charts: res.charts || [] }]);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', text: '⚠️ ' + (res.error || 'AI 服务异常，请稍后再试'), charts: [] }]);
      }
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', text: '⚠️ 网络错误，请稍后再试', charts: [] }]);
    }
    setLoading(false);
  };

  return (
    <div className="page-container">
      {/* 微信日报推送（PushPlus）配置 */}
      <Card size="small" style={{ marginBottom: 12 }}
        title={<span><NotificationOutlined style={{ color: 'var(--brand)', marginRight: 8 }} />微信日报推送（PushPlus）{pushCfg.configured && <Tag color={pushCfg.enabled ? 'green' : 'orange'} style={{ marginLeft: 8 }}>{pushCfg.enabled ? '已启用' : '已配置未启用'}</Tag>}</span>}
        extra={<Button size="small" onClick={() => setPushOpen(o => !o)}>{pushOpen ? '收起' : '配置'}</Button>}>
        {pushOpen ? (
          <div>
            <div style={{ color: '#8c8c8c', fontSize: 12, marginBottom: 10 }}>
              关注 PushPlus 公众号拿 token（pushplus.plus）→ 填到下面 → 开启后每天 08:00 自动把日报推送到你的微信。
              {pushCfg.configured && <span>当前已配置 token：{pushCfg.token_masked}</span>}
            </div>
            <Space wrap>
              <Input.Password style={{ width: 320 }} placeholder={pushCfg.configured ? '已配置，重新粘贴可更换' : '粘贴 PushPlus token'}
                value={pushToken} onChange={e => setPushToken(e.target.value)} />
              <span>启用推送 <Switch checked={pushCfg.enabled} onChange={v => setPushCfg(c => ({ ...c, enabled: v }))} /></span>
              <Button type="primary" loading={pushSaving} onClick={savePushCfg}>保存</Button>
              <Button loading={pushTesting} onClick={testPush} disabled={!pushCfg.configured}>测试发送</Button>
            </Space>
          </div>
        ) : (
          <span style={{ color: '#8c8c8c', fontSize: 12 }}>
            {pushCfg.configured ? (pushCfg.enabled ? '已开启，每天 08:00 推送昨日日报到微信' : '已配置 token 但未开启推送') : '未配置 —— 点右上角「配置」填入 PushPlus token'}
          </span>
        )}
      </Card>

      <Card
        title={<span><RobotOutlined style={{ color: 'var(--brand)', marginRight: 8 }} />AI 数据分析助手</span>}
        extra={<Tag color="#EF9F27">仅管理员可用</Tag>}
        style={{ height: 'calc(100vh - 130px)', display: 'flex', flexDirection: 'column' }}
        bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column', padding: 12, overflow: 'hidden' }}
      >
        <div ref={boxRef} style={{ flex: 1, overflowY: 'auto', padding: '4px 8px' }}>
          {messages.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#8c8c8c' }}>
              <RobotOutlined style={{ fontSize: 48, color: '#E8A87C', marginBottom: 12 }} />
              <div style={{ fontSize: 15, marginBottom: 16 }}>用自然语言提问，我帮你查数据、给结论 📊</div>
              <Space wrap size={8}>
                {SUGGESTIONS.map(s => (
                  <Button key={s} size="small" onClick={() => send(s)} style={{ borderRadius: 14 }}>{s}</Button>
                ))}
              </Space>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} style={{ display: 'flex', marginBottom: 14, justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
              <div style={{ maxWidth: '82%', display: 'flex', gap: 8, flexDirection: m.role === 'user' ? 'row-reverse' : 'row' }}>
                <div style={{
                  width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                  background: m.role === 'user' ? 'var(--brand)' : '#FAEEDA', color: m.role === 'user' ? '#fff' : '#854F0B',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {m.role === 'user' ? <UserOutlined /> : <RobotOutlined />}
                </div>
                <div style={{
                  background: m.role === 'user' ? 'var(--brand)' : '#fff', color: m.role === 'user' ? '#fff' : '#333',
                  borderRadius: 12, padding: '10px 14px', fontSize: 13.5, lineHeight: 1.7,
                  boxShadow: m.role === 'user' ? 'none' : '0 1px 4px rgba(0,0,0,0.08)', border: m.role === 'user' ? 'none' : '0.5px solid #f0e6df',
                }}>
                  {renderText(m.text)}
                  {m.charts && m.charts.map((c, ci) => (
                    <div key={ci} style={{ marginTop: 8, background: '#FAF7F5', borderRadius: 8, padding: 6 }}>
                      <div style={{ fontWeight: 600, fontSize: 12.5, color: '#59350a', margin: '4px 0 0 6px' }}>{c.title}</div>
                      <ChartBlock chart={c} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
          {loading && (
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#FAEEDA', color: '#854F0B', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><RobotOutlined /></div>
              <div style={{ background: '#fff', borderRadius: 12, padding: '10px 14px', border: '0.5px solid #f0e6df' }}><Spin size="small" /> <span style={{ marginLeft: 8, color: '#8c8c8c' }}>正在查数据、分析中…</span></div>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, paddingTop: 10, borderTop: '0.5px solid #f0f0f0' }}>
          <Input
            placeholder="例如：分析集客舒酒店用品旗舰店8月两面针的销售情况"
            value={input}
            onChange={e => setInput(e.target.value)}
            onPressEnter={() => send()}
            allowClear
          />
          <Button type="primary" icon={<SendOutlined />} loading={loading} onClick={() => send()}>发送</Button>
        </div>
      </Card>
    </div>
  );
}
